import {
  parseDateOnlyString,
  type WorkplaceRequestTypeFieldType
} from "@gettactic/api";
import { time24To12 } from "../date-time/timeStringConverters";
import { toReservationFieldFormValue } from "./reservationFieldValue";

/**
 * The value a form input expects for an answer read back from a request. The
 * read counterpart of {@link toWorkplaceRequestFieldValue} — a value headed
 * back to the API has to go through that one, not this.
 *
 * Four field types don't read back in the shape they were written:
 *
 * - A `person` answer is stored as a user id, but the API resolves it into the
 *   full user object on read, so the picker would show it as empty.
 * - A `date` answer is stored as a bare `YYYY-MM-DD`, while the calendar input
 *   holds a `Date` and ignores anything else. It is parsed at local midnight
 *   rather than through `new Date`, which reads the bare form as UTC and shows
 *   the day before to everyone west of UTC.
 * - A `time` answer is stored as 24-hour `HH:MM` (or `HH:MM:SS`), while the
 *   shared time picker holds a 12-hour string.
 * - A `reservation` answer is an object, and every part of it has one of the
 *   problems above at once.
 */
export const toWorkplaceRequestFormValue = (
  fieldType: WorkplaceRequestTypeFieldType,
  value: unknown
): unknown => {
  if (fieldType === "reservation") return toReservationFieldFormValue(value);
  if (fieldType === "date") {
    if (typeof value !== "string" || !value) return value;
    const parsed = parseDateOnlyString(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed;
  }
  if (fieldType === "time") {
    if (typeof value !== "string" || !value) return value;
    return time24To12(value);
  }
  if (fieldType !== "person") return value;
  if (value && typeof value === "object") {
    return (value as { id?: string }).id ?? "";
  }
  return value;
};
